'use client'

import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import { env } from '~/env'
import 'mapbox-gl/dist/mapbox-gl.css'

interface BackgroundMapProps {
  /** Latitude coordinate for the center of the map */
  latitude: number
  /** Longitude coordinate for the center of the map */
  longitude: number
  /** Radius in miles to display as a service area (optional) */
  radiusMiles?: number
  /** Whether to show a pin at the center location */
  showPin?: boolean
  /** Custom zoom level (default: 10) */
  zoom?: number
  /** Map style (default: 'mapbox://styles/mapbox/light-v11') */
  mapStyle?: string
  /** Map size variant: 'full' for full width, 'square' for approximately square, 'bottom' for bottom edge-to-edge with top fade, 'card' for card background with left fade */
  size?: 'full' | 'square' | 'bottom' | 'card'
  /** Whether to show the fade overlay (default: true) */
  showFade?: boolean
  /** Additional CSS classes */
  className?: string
}

export default function BackgroundMap({
  latitude,
  longitude,
  radiusMiles,
  showPin = true,
  zoom = 10,
  mapStyle = 'mapbox://styles/mapbox/light-v11',
  size = 'full',
  showFade = true,
  className = '',
}: BackgroundMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const radiusRef = useRef<number | undefined>(radiusMiles)
  
  // Update ref when radius changes
  useEffect(() => {
    radiusRef.current = radiusMiles
  }, [radiusMiles])

  // Initialize map (only once, but wait for valid coordinates)
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    // Don't initialize with invalid coordinates
    if (latitude === 0 && longitude === 0) return

    // Set access token
    mapboxgl.accessToken = env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN

    // Calculate initial zoom level based on whether we have a radius
    let initialZoom = zoom
    if (radiusMiles) {
      // For service areas, start with a wider view
      const radiusDegrees = radiusMiles / 69
      initialZoom = Math.log2(360 / (radiusDegrees * 3)) // 3 gives more padding for initial view
      initialZoom = Math.min(Math.max(initialZoom, 8), 12) // Keep between zoom 8-12
    }

    try {
      // Create map instance
      // For square maps, offset center to account for left fade
      let centerLongitude = longitude
      const centerLatitude = latitude
      
      if (size === 'square') {
        // Move center right by a more noticeable amount to account for left fade
        // The fade is 128px wide, so we need to move the center right
        centerLongitude = longitude + 0.02 // More noticeable offset
      } else if (size === 'bottom' || size === 'card') {
        // For bottom and card maps, center normally (no offset needed)
        centerLongitude = longitude
      }

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: [centerLongitude, centerLatitude],
        zoom: initialZoom,
        interactive: false, // Non-interactive as requested
        attributionControl: false, // Hide attribution for background use
        logoPosition: 'bottom-right',
      })

      // Wait for map to load - this ensures style is loaded
      map.current.once('load', () => {
        setMapLoaded(true)
      })

      // Handle map errors
      map.current.on('error', (e) => {
        console.error('Map error:', e)
      })

    } catch (error) {
      console.error('Error creating map:', error)
    }

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
        setMapLoaded(false)
      }
    }
  }, [latitude, longitude, zoom, mapStyle, radiusMiles, size]) // Re-run if coordinates change from 0,0 to valid values

  // Update map center when coordinates change (after map is initialized)
  useEffect(() => {
    if (!map.current || !mapLoaded) return
    if (latitude === 0 && longitude === 0) return

    let centerLongitude = longitude
    if (size === 'square') {
      centerLongitude = longitude + 0.02
    }

    map.current.setCenter([centerLongitude, latitude])
  }, [latitude, longitude, mapLoaded, size])

  // Add/update pin marker when map is loaded or coordinates change
  useEffect(() => {
    if (!map.current || !mapLoaded || !showPin) return
    if (latitude === 0 && longitude === 0) return

    // Use the same offset center for the pin marker
    let markerLongitude = longitude
    if (size === 'square') {
      markerLongitude = longitude + 0.02 // Same offset as map center
    } else if (size === 'bottom' || size === 'card') {
      markerLongitude = longitude
    }

    // Remove existing marker if it exists
    if (markerRef.current) {
      markerRef.current.remove()
      markerRef.current = null
    }

    // Create a custom marker element
    const markerEl = document.createElement('div')
    markerEl.className = 'custom-marker'
    markerEl.innerHTML = `
      <div class="w-6 h-6 bg-primary rounded-full border-2 border-white shadow-lg flex items-center justify-center">
        <div class="w-2 h-2 bg-white rounded-full"></div>
      </div>
    `

    // Add marker to map
    markerRef.current = new mapboxgl.Marker(markerEl)
      .setLngLat([markerLongitude, latitude])
      .addTo(map.current)
  }, [mapLoaded, showPin, longitude, latitude, size])

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.remove()
        markerRef.current = null
      }
    }
  }, [])

  // Add service area circle when map is loaded and radius is provided
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Helper function to create circle geometry
    const createCircleGeometry = (radiusMiles: number): [number, number][] => {
      const radiusDegrees = radiusMiles / 69
      let circleLongitude = longitude
      if (size === 'square') {
        circleLongitude = longitude + 0.02 // Same offset as map center
      } else if (size === 'bottom' || size === 'card') {
        circleLongitude = longitude
      }

      const circlePoints: [number, number][] = []
      const numPoints = 64 // Number of points to create a smooth circle
      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI
        const lat = latitude + (radiusDegrees * Math.sin(angle))
        const lng = circleLongitude + (radiusDegrees * Math.cos(angle) / Math.cos(latitude * Math.PI / 180))
        circlePoints.push([lng, lat])
      }
      // Close the circle by adding the first point again
      if (circlePoints.length > 0) {
        const firstPoint = circlePoints[0]
        if (firstPoint) {
          circlePoints.push([firstPoint[0], firstPoint[1]])
        }
      }
      return circlePoints
    }

    // Function to update the service area circle
    const updateServiceArea = () => {
      if (!map.current) {
        console.log('Map not available for service area update')
        return
      }

      if (!radiusMiles || radiusMiles <= 0) {
        console.log('No radius provided, cleaning up')
        // Clean up if radius is removed
        try {
          if (map.current.getLayer('service-area-outline')) {
            map.current.removeLayer('service-area-outline')
          }
          if (map.current.getLayer('service-area-circle')) {
            map.current.removeLayer('service-area-circle')
          }
          if (map.current.getSource('service-area')) {
            map.current.removeSource('service-area')
          }
        } catch {
          // Ignore errors during cleanup
        }
        return
      }

      try {
        const circlePoints = createCircleGeometry(radiusMiles)
        const sourceExists = map.current.getSource('service-area')

        if (sourceExists) {
          // Update existing source data
          const source = sourceExists as mapboxgl.GeoJSONSource
          source.setData({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [circlePoints],
            },
            properties: {},
          })
        } else {
          console.log('Creating new service area source and layers')
          // Create new source and layers
          map.current.addSource('service-area', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [circlePoints],
              },
              properties: {},
            },
          })

          // Add fill layer
          map.current.addLayer({
            id: 'service-area-circle',
            type: 'fill',
            source: 'service-area',
            paint: {
              'fill-color': 'rgba(142, 156, 135, 0.2)', // primary with transparency
              'fill-opacity': 0.8,
            },
          })

          // Add outline layer
          map.current.addLayer({
            id: 'service-area-outline',
            type: 'line',
            source: 'service-area',
            paint: {
              'line-color': 'rgba(142, 156, 135, 0.8)', // primary
              'line-width': 2,
            },
          })
          console.log('Service area layers added successfully')
        }

        // Update zoom/bounds to fit the circle
        const radiusDegrees = radiusMiles / 69
        let circleLongitude = longitude
        if (size === 'square') {
          circleLongitude = longitude + 0.02
        } else if (size === 'bottom' || size === 'card') {
          circleLongitude = longitude
        }
        
        // Tighter fit for card size, more padding for others
        const radiusDegreesForBounds = radiusDegrees * (size === 'card' ? 1.0 : 1.2)
        const bounds: [number, number, number, number] = [
          circleLongitude - radiusDegreesForBounds,
          latitude - radiusDegreesForBounds,
          circleLongitude + radiusDegreesForBounds,
          latitude + radiusDegreesForBounds
        ]
        
        map.current.fitBounds(bounds, {
          padding: size === 'card' ? 10 : 50,
          duration: 1000,
          essential: true
        })
      } catch (error) {
        console.error('Error updating service area on map:', error)
      }
    }

    // Execute the update - ensure style is loaded before adding sources
    if (map.current?.isStyleLoaded()) {
      updateServiceArea()
    } else if (map.current) {
      // Wait for style to be ready
      const checkAndUpdate = () => {
        if (map.current?.isStyleLoaded()) {
          updateServiceArea()
        } else if (map.current) {
          // Try again after a short delay
          setTimeout(checkAndUpdate, 100)
        }
      }
      checkAndUpdate()
    }
  }, [mapLoaded, radiusMiles, longitude, latitude, size])

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* For square maps, wrap map and fade in a container */}
      {size === 'square' ? (
        <div className="relative h-full w-auto aspect-square ml-auto" style={{ minWidth: '400px' }}>
          {/* Map container */}
          <div
            ref={mapContainer}
            className="h-full w-full"
            style={{ minHeight: '300px' }}
          />
          
          {/* Fade overlay - now positioned relative to the wrapper */}
          {showFade && (
            <div className="absolute inset-0 pointer-events-none z-10">
              {/* Top fade */}
              <div className="absolute top-0 left-0 right-0 h-32 bg-linear-to-b from-white via-white/80 to-transparent" />
              
              {/* Left fade */}
              <div className="absolute left-0 top-0 bottom-0 w-32 bg-linear-to-r from-white via-white/80 to-transparent" />
              
              {/* Corner fade */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-linear-to-br from-white via-white/90 to-transparent" />
            </div>
          )}
        </div>
      ) : size === 'bottom' ? (
        <>
          {/* Map container for bottom mode - full width, centered */}
          <div
            ref={mapContainer}
            className="h-full w-full"
            style={{ minHeight: '300px' }}
          />
          
          {/* Fade overlay for bottom mode - top fade only */}
          {showFade && (
            <div className="absolute inset-0 pointer-events-none z-10">
              {/* Top fade only */}
              <div className="absolute top-0 left-0 right-0 h-32 bg-linear-to-b from-white via-white/80 to-transparent" />
            </div>
          )}
        </>
      ) : size === 'card' ? (
        <>
          {/* Map container for card mode - centered */}
          <div
            ref={mapContainer}
            className="h-full w-full"
          />
          
          {/* Fade overlay for card mode - left fade */}
          {showFade && (
            <div className="absolute inset-0 pointer-events-none z-10">
              {/* Left fade */}
              <div className="absolute left-0 top-0 bottom-0 w-1/2 bg-linear-to-r from-white via-white/60 to-transparent" />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Map container for full width */}
          <div
            ref={mapContainer}
            className="h-full w-full"
            style={{ minHeight: '300px' }}
          />
          
          {/* Fade overlay for full width */}
          {showFade && (
            <div className="absolute inset-0 pointer-events-none z-10">
              {/* Top fade only */}
              <div className="absolute top-0 left-0 right-0 h-32 bg-linear-to-b from-white via-white/80 to-transparent" />
            </div>
          )}
        </>
      )}
    </div>
  )
}
